import styled, { CSSProperties } from 'styled-components';
import { useMemo, useEffect, useRef, useState, RefObject, useLayoutEffect, useId } from 'react';
import { Text } from './Text';
import { getOffset } from '../../scripts/scripts';

const StyledSelect = styled.div<{ disabled?: boolean }>`
	width: 100%;
	min-height: 100%;
	display: flex;
	position: relative;
	${({ disabled }) => disabled && 'opacity: 0.4;'}
`;

const SelectedValuesContainer = styled.div<{ disabled?: boolean }>`
	width: 100%;
	min-height: 100%;

	padding: 0 1rem;

	display: flex;
	align-items: center;

	border: none;
	border-bottom: 2px solid ${({ theme }) => theme.text_theme};

	background: ${({ theme }) => theme.input_bg};

	cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
	position: relative;
	overflow: hidden;
	p {
		overflow: hidden;
		text-overflow: ellipsis;
		width: 100%;
	}
	* {
		${({ disabled }) => (disabled ? 'cursor: not-allowed!important;' : '')};
	}
	::after {
		content: '';
		width: 4px !important;
		height: 4px !important;
		border-right: 2px solid ${({ theme }) => theme.input_fg};
		border-bottom: 2px solid ${({ theme }) => theme.input_fg};

		-webkit-transform: rotate(45deg);
		-moz-transform: rotate(45deg);
		-ms-transform: rotate(45deg);
		transform: rotate(45deg) translateY(-50%);
		top: 50%;
		right: 10px;

		position: absolute;
		pointer-events: none;
	}
`;

const SelectDropdown = styled.div<{ $selectActive: boolean }>`
	visibility: ${({ $selectActive }) => ($selectActive ? 'visible' : 'hidden')};
	opacity: ${({ $selectActive }) => ($selectActive ? '1' : '0')};
	display: flex;
	flex-direction: column;
	position: fixed;

	z-index: 1;

	border: 1px solid ${(props) => props.theme.select_dropdown_border};
	background: ${({ theme }) => theme.select_dropdown_bg};
	box-shadow: 0 0 10px 8px rgba(9, 10, 22, 0.25);
`;

const SearchBox = styled.input`
	width: 100%;

	padding: 0.75rem 1rem;

	display: flex;
	align-items: center;
	position: relative;
	border: 1px solid transparent;

	outline: 0;
	background-color: transparent;
	border: none;

	color: ${({ theme }) => theme.input_fg};
	font-size: 1.2rem;
	font-weight: 400;

	&::placeholder {
		color: ${({ theme }) => theme.input_placeholder};
	}

	::after {
		content: '';
		width: 6px;
		height: 6px;
		border-right: 2px solid ${({ theme }) => theme.input_fg};
		border-bottom: 2px solid ${({ theme }) => theme.input_fg};

		-webkit-transform: rotate(45deg);
		-moz-transform: rotate(45deg);
		-ms-transform: rotate(45deg);
		transform: rotate(45deg);
		top: 9px;
		right: 13px;

		position: absolute;
		pointer-events: none;
	}
`;

const OptionsBox = styled.div`
	width: 100%;
	height: 100%;
	box-sizing: border-box;
	position: relative;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
`;

const OptionBox = styled.div<{ $selected: boolean; $lineHeight: string | number }>`
	width: 100%;
	height: ${({ $lineHeight }) => $lineHeight}px !important;

	padding: 0 1rem;

	flex-shrink: 0;

	display: flex;
	align-items: center;

	cursor: pointer;
	overflow: hidden;
	text-overflow: ellipsis;

	background-color: ${({ $selected, theme }) => ($selected ? theme.select_dropdown_bg_selected : 'transparent')};

	// NOTE: Selection Icon
	&::after {
		content: '';
		width: 8px;
		height: 8px;
		margin: auto 0 auto auto;
		display: block;
		border-radius: 50%;
		background-color: ${({ $selected, theme }) => ($selected ? theme.text_theme : 'transparent')};
	}
	&:hover {
		background: ${({ theme }) => theme.select_dropdown_bg_hover};
	}
`;

const calcDropdownPosition = (
	{
		selectRef,
		dropdownRef,
		inputRef,
		searchBoxRef,
		optionsRef,
	}: {
		selectRef: RefObject<HTMLDivElement | null>;
		dropdownRef: RefObject<HTMLDivElement | null>;
		inputRef: RefObject<HTMLDivElement | null>;
		searchBoxRef: RefObject<HTMLDivElement | null>;
		optionsRef: RefObject<HTMLDivElement | null>;
	},
	isActive?: boolean,
) => {
	if (!selectRef.current || !dropdownRef.current || !inputRef.current || !searchBoxRef.current || !optionsRef.current)
		return;

	const active = isActive !== undefined ? isActive : selectRef.current.dataset.active == 'true';
	if (!active) return;

	const containerOffset = getOffset(selectRef.current);

	// NOTE: Input ref stuff
	const documentWidth = selectRef.current.ownerDocument.defaultView!.innerWidth;
	const distanceFromTop = containerOffset.top;
	const distanceFromBottom = window.innerHeight - distanceFromTop;
	const dropdownHeight = dropdownRef.current.offsetHeight;
	const selectionContHeight = inputRef.current.offsetHeight;

	// NOTE: Alignment
	dropdownRef.current.style.bottom = `unset`;
	dropdownRef.current.style.left = `${containerOffset.left}px`;
	dropdownRef.current.style.right = `${documentWidth - containerOffset.left - selectRef.current.offsetWidth}px`;

	// NOTE: Dropdown height
	const maxHeightContainer = 500;
	const minHeightContainer = 250;
	const documentPadding = 35;

	// NOTE: Container is on top
	if (distanceFromBottom < minHeightContainer) {
		dropdownRef.current.style.top = `${distanceFromTop - dropdownHeight}px`;

		searchBoxRef.current.style.order = '2';

		dropdownRef.current.style.maxHeight =
			distanceFromTop > maxHeightContainer
				? `${maxHeightContainer}px`
				: `${minHeightContainer + (distanceFromTop - minHeightContainer - documentPadding)}px`;
	} else {
		dropdownRef.current.style.top = `${distanceFromTop + selectionContHeight}px`;

		dropdownRef.current.style.maxHeight =
			distanceFromBottom - selectionContHeight > maxHeightContainer
				? `${maxHeightContainer}px`
				: `${minHeightContainer + (distanceFromBottom - minHeightContainer - selectionContHeight - documentPadding)}px`;

		searchBoxRef.current.style.order = 'initial';
	}
};

export const selectOptionLineHeight = 35;

export interface SelectOption {
	value: string;
	text: string;
	hidden?: boolean;
}

export interface BaseSelectProps {
	options: SelectOption[];
	disabled?: boolean;
	disableClick?: boolean;
	id?: string;
	className?: string;
	style?: CSSProperties;
	onFocus?: () => void;
}

export type SingleValueSelectProps = {
	value: string;
	multi?: false;
	onChange: (value: string) => void;
};

export type MultipleValueSelectProps = {
	value: string[];
	multi: true;
	onChange: (value: string[]) => void;
};

export function SelectX6({
	disabled,
	multi,
	onChange,
	onFocus: onFocusProp,
	value,
	options,
	style,
	disableClick,
	id: idProp,
}: BaseSelectProps & (SingleValueSelectProps | MultipleValueSelectProps)) {
	const [selectActive, setSelectActive] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');

	const selectRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLDivElement>(null);
	const optionsRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const searchBoxRef = useRef<HTMLInputElement>(null);

	const generatedId = useId();
	const ID = idProp ?? generatedId;

	useEffect(() => {
		if (!!disabled && selectActive) {
			// eslint-disable-next-line
			setSelectActive(false);
		}
	}, [disabled, selectActive]);

	const optionClickMulti = (optionValue: string) => {
		if (!multi) return;

		let newSelectedValue: string[];
		if (optionValue === '' && value.includes('') && value.length >= 2) {
			newSelectedValue = value.filter((value: string) => value !== '');
		} else if (optionValue === '') {
			newSelectedValue = [''];
		} else if (value.includes(optionValue)) {
			const newItems = value.filter((value) => value !== optionValue);
			if (newItems.length < 1) newItems.push('');
			newSelectedValue = newItems;
		} else {
			const newItems = value.filter((value: string) => value !== '');
			newItems.push(optionValue);
			newSelectedValue = newItems;
		}
		onChange(newSelectedValue);
	};

	const optionClickSingle = (optionValue: string) => {
		if (multi) return;

		setSelectActive(false);
		calcDropdownPosition({ selectRef, dropdownRef, inputRef, searchBoxRef, optionsRef }, false);
		onChange(optionValue);
	};

	const optionClick = (optionValue: string) => {
		if (multi) optionClickMulti(optionValue);
		else optionClickSingle(optionValue);
	};

	const visibleOptions = useMemo(() => {
		if (!options) return [];
		const lowercCaseSearchTerm = searchTerm.toLowerCase();
		return options.filter((option) => {
			const optionIsSelected = multi ? value.includes(option.value) : value === option.value;
			const hidden = option.hidden !== undefined ? option.hidden : false;
			const isSelected = optionIsSelected;
			const inSearch =
				option.text?.toLowerCase().includes(lowercCaseSearchTerm) ||
				option.value?.toLowerCase().includes(lowercCaseSearchTerm);
			return inSearch && (hidden === false || (option.hidden === true && isSelected)) ? true : false;
		});
	}, [options, searchTerm, multi, value]);

	const optionsElements = visibleOptions.map((item) => (
		<OptionBox
			key={`select_${ID}_${item.value}`}
			onClick={() => optionClick(item.value)}
			$selected={multi ? value.includes(item.value) : value === item.value}
			$lineHeight={selectOptionLineHeight}
		>
			<Text
				$color="input_fg"
				$size="1.2rem"
				$weight="400"
				style={{ whiteSpace: 'nowrap', flex: 1, marginRight: '0.5rem' }}
			>
				{item.text}
			</Text>
		</OptionBox>
	));

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (disableClick !== true && selectRef.current && !selectRef.current.contains(event.target! as Node)) {
				setSelectActive(false);
			}
		};

		const handlePosition = () => {
			calcDropdownPosition({ selectRef, dropdownRef, inputRef, searchBoxRef, optionsRef });
		};

		window.addEventListener('resize', handlePosition);
		document.addEventListener('scroll', handlePosition, true);
		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('scroll', handlePosition, true);
			window.removeEventListener('resize', handlePosition);
		};
	}, [disableClick]);

	useLayoutEffect(() => {
		calcDropdownPosition({
			selectRef,
			dropdownRef,
			inputRef,
			searchBoxRef,
			optionsRef,
		});
	}, [visibleOptions.length]);

	// NOTE: Focus search box on open
	useEffect(() => {
		if (searchBoxRef.current !== null) {
			if (selectActive === true) searchBoxRef.current.focus();
			else searchBoxRef.current.blur();
		}
	}, [selectActive]);

	const getSelectedValueString = useMemo(() => {
		if (multi) {
			const joinedString: string[] = [];
			for (const selectedValue of value) {
				const selectedOption = options ? options.find((option) => option.value === selectedValue) : null;
				if (selectedOption) joinedString.push(selectedOption.text);
			}
			return joinedString.join(', ');
		} else {
			const selectedOption = options ? options.find((option) => option.value === value) : null;
			return selectedOption ? selectedOption.text : '';
		}
	}, [options, value, multi]);

	const onClickInputBox = () => {
		if (disabled) return;

		const newState = !selectActive;
		setSelectActive(newState);
		calcDropdownPosition({ selectRef, dropdownRef, inputRef, searchBoxRef, optionsRef }, newState);
		if (onFocusProp) onFocusProp();
	};

	return (
		<StyledSelect ref={selectRef} data-active={selectActive} disabled={disabled} style={style && style}>
			<SelectedValuesContainer ref={inputRef} onClick={() => onClickInputBox()} disabled={disabled}>
				<Text $color={'select_fg'} $size="1.2rem" $weight="400" style={{ whiteSpace: 'nowrap' }}>
					{getSelectedValueString}
				</Text>
			</SelectedValuesContainer>
			<SelectDropdown ref={dropdownRef} $selectActive={selectActive}>
				<SearchBox
					ref={searchBoxRef}
					placeholder="Search..."
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
				/>
				<OptionsBox ref={optionsRef}>{optionsElements}</OptionsBox>
			</SelectDropdown>
		</StyledSelect>
	);
}
